import * as _ from 'lodash-es';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import EmptyState from '../../components/EmptyState';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import PostSortMenuButton from '../../components/PostSortMenuButton';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { PostFilter, PostSort } from '../../graphql/generated/graphql';
import postWithContentIcon from '../../icons/non-prefilled/16x16/postWithContent.svg';
import verifiedIcon from '../../icons/non-prefilled/16x16/verified.svg';
import vipCrownQueenIcon from '../../icons/non-prefilled/16x16/vipCrownQueen.svg';
import PostRow from '../../post/PostRow';
import PostRowMeta from '../../post/PostRowMeta';
import PostSectionHeader from '../../post/PostSectionHeader';
import PostSectionTitleFilterMenu from '../../post/PostSectionTitleFilterMenu';
import ConnectionHelper from '../../utils/graphql/ConnectionHelper';

const USER_POSTS_QUERY = gql(/** @complexity 8 */ `
  query UserPosts(
    $afterCursor: String
    $filters: [PostFilter!]
    $sortBy: PostSort
    $userId: ID!
  ) {
    listPosts(
      first: 100,
      after: $afterCursor
      filters: $filters
      sortBy: $sortBy
      userId: $userId
    ) {
      edges {
        node {
          id
          views
          editedAt
          publishedAt
          ...PostRow_post
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const USER_FRAGMENT = gql(/** @complexity 1 */ `
  fragment UserPostsList_user on User {
    id
    name
  }
`);

interface UserPostsListProps {
  user: DocumentType<typeof USER_FRAGMENT>;
}

const METADATA_ATTRIBUTE = {
  [PostSort.Popularity]: 'popularity',
  [PostSort.Published]: 'publishedAt',
  [PostSort.Edited]: 'editedAt',
  [PostSort.Alphabetical]: 'editedAt',
} as const;

const ROLE_OPTIONS = [
  {
    label: 'Owner',
    value: PostFilter.Owner,
    icon: vipCrownQueenIcon,
  },
  {
    label: 'Verifier',
    value: PostFilter.Verifier,
    icon: verifiedIcon,
  },
  {
    label: 'Contributor',
    value: PostFilter.Contributor,
    icon: postWithContentIcon,
  },
] as const;

const DEFAULT_ORDER = PostSort.Published;

const getEmptyMessage = (
  role: (typeof ROLE_OPTIONS)[number]['value'],
  name: string,
) => {
  switch (role) {
    case PostFilter.Owner:
      return `${name} isn’t the owner of any posts yet.`;
    case PostFilter.Verifier:
      return `${name} hasn’t verified any posts yet.`;
    case PostFilter.Contributor:
      return `${name} isn’t a contributor on any posts yet.`;
    default:
      break;
  }
  return null;
};

interface UsePostListQueryProps {
  shouldIncludeArchived: boolean;
  shouldPrefetch: boolean;
  roleOption: (typeof ROLE_OPTIONS)[number];
  sortBy: PostSort;
  selectedRoleOption: (typeof ROLE_OPTIONS)[number];
  userId: string;
}

const usePostListQuery = ({
  shouldIncludeArchived,
  shouldPrefetch,
  roleOption,
  selectedRoleOption,
  sortBy,
  userId,
}: UsePostListQueryProps) => {
  const result = useQuery(USER_POSTS_QUERY, {
    variables: {
      filters: shouldIncludeArchived
        ? [roleOption.value]
        : [roleOption.value, PostFilter.Unarchived],
      sortBy,
      userId,
    },
    skip: selectedRoleOption.value !== roleOption.value && !shouldPrefetch,
  });

  const fetchMore = ConnectionHelper.generateFetchMore(
    USER_POSTS_QUERY,
    result,
    'listPosts',
  );

  const posts = _.compact(
    result.data?.listPosts?.edges?.map((edge) => edge?.node),
  );

  return { posts, fetchMore, loading: result.loading };
};

const POST_SORT_OPTIONS = [
  PostSort.Popularity,
  PostSort.Published,
  PostSort.Edited,
  PostSort.Alphabetical,
] satisfies PostSort[];

const UserPostsList = ({ user }: UserPostsListProps) => {
  const [sortBy, setSortBy] =
    useState<(typeof POST_SORT_OPTIONS)[number]>(DEFAULT_ORDER);

  const [selectedRoleOption, setSelectedRoleOption] = useState<
    (typeof ROLE_OPTIONS)[number]
  >(ROLE_OPTIONS[0]);
  const [shouldIncludeArchived, setShouldIncludeArchived] = useState(false);
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  const commonOptions = {
    shouldIncludeArchived,
    shouldPrefetch,
    selectedRoleOption,
    sortBy,
    userId: user.id,
  };

  const ownerResult = usePostListQuery({
    ...commonOptions,
    roleOption: ROLE_OPTIONS[0],
  });

  const verifierResult = usePostListQuery({
    ...commonOptions,
    roleOption: ROLE_OPTIONS[1],
  });

  const contributorResult = usePostListQuery({
    ...commonOptions,
    roleOption: ROLE_OPTIONS[2],
  });

  const results = {
    [ROLE_OPTIONS[0].label]: ownerResult,
    [ROLE_OPTIONS[1].label]: verifierResult,
    [ROLE_OPTIONS[2].label]: contributorResult,
  };

  const currentResult = results[selectedRoleOption.label];

  return (
    <PostRow.Provider>
      <PostSectionHeader
        title={
          <PostSectionTitleFilterMenu
            onShouldPrefetch={() => setShouldPrefetch(true)}
            options={ROLE_OPTIONS}
            selectedOption={selectedRoleOption}
            onSelectedOptionChange={setSelectedRoleOption}
            getCount={(option) => results[option.label].posts.length}
          />
        }
        controls={
          <PostSortMenuButton
            allowedPostSorts={POST_SORT_OPTIONS}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            isArchivedIncluded={shouldIncludeArchived}
            onIsArchivedIncludedChange={setShouldIncludeArchived}
          />
        }
      />
      {currentResult.posts.map((post) => (
        <PostRow
          key={post.id}
          post={post}
          meta={
            <PostRowMeta
              attribute={METADATA_ATTRIBUTE[sortBy]}
              values={{
                publishedAt: post.publishedAt,
                editedAt: post.editedAt,
                popularity: post.views,
              }}
            />
          }
        />
      ))}
      {currentResult.posts.length === 0 && !currentResult.loading ? (
        <EmptyState
          imageName="posts"
          title="No posts here…"
          message={getEmptyMessage(selectedRoleOption.value, user.name)}
        />
      ) : (
        <InfiniteScrollWaypoint fetchMore={currentResult.fetchMore} />
      )}
    </PostRow.Provider>
  );
};

export default UserPostsList;
